/**
 * `PlanOffer` — the ready plans a guide OFFERS after the questionnaire, not yet
 * taken (`peterhof_plan`, #87).
 *
 * The stops are the reason this card exists: the model used to retell them and
 * swapped one (#78), so the code draws every stop of every plan and the model
 * only introduces them. Printed as they came — the numbers are the offer's
 * («давай второй» names `n`), the notes are the code's own words for the date.
 *
 * **No choose button here, on purpose.** The chat's footer chips («Беру «…»»,
 * one per plan) are the choice; a second set on the card would say it twice and
 * outlive the turn in the scrollback, where the footer's are already gone.
 *
 * **Photographs** (#155): a plan's `images` — the cover (its first stop with a
 * photograph), then its next stops' — as a row that scrolls sideways, a swipe on
 * a phone. No state beyond a photograph that failed to load, no likes (owner,
 * 28.09.2026). A plan without them is drawn exactly as before.
 *
 * **Frames of a fixed width** (#202, owner 08.10.2026): the cover used to take
 * 80% of the row, so on a desktop it was a ~500px picture with a sliver of the
 * next one and nothing saying the row moves. A frame is now as wide as a strip
 * tile on any screen, and a mouse gets what a finger has: «‹ ›» at the row's
 * edges (only while there is somewhere to go) and the wheel moves the row
 * sideways until its end, then the chat. The arrows are drawn after the row has
 * been measured, so the server-rendered card has no buttons at all.
 */

import { useEffect, useRef, useState } from 'react';
import type { PlanOfferProps } from '../generated/schemas';
import { cn } from '../../lib/cn';
import { stripEmoji as plain } from '../../lib/emoji';

type OfferedPlan = PlanOfferProps['plans'][number];

/** Which way the row can still move — an edge within 2px counts as reached
 *  (sub-pixel widths never land on zero exactly). */
export function photoRowEdges(row: { scrollLeft: number; clientWidth: number; scrollWidth: number }): {
  prev: boolean;
  next: boolean;
} {
  return { prev: row.scrollLeft > 2, next: row.scrollLeft + row.clientWidth < row.scrollWidth - 2 };
}

/** How far a wheel turn moves the row sideways — `0` hands it back to the page:
 *  a horizontal gesture is the trackpad's own, and a row at its end lets the
 *  chat scroll on. */
export function photoRowWheel(
  row: { scrollLeft: number; clientWidth: number; scrollWidth: number },
  wheel: { deltaX: number; deltaY: number },
): number {
  if (Math.abs(wheel.deltaX) >= Math.abs(wheel.deltaY)) return 0;
  const edges = photoRowEdges(row);
  if ((wheel.deltaY > 0 && !edges.next) || (wheel.deltaY < 0 && !edges.prev)) return 0;
  return wheel.deltaY;
}

/** The plan's photographs, cover first — nothing at all when it has none. */
function OfferPhotos({ plan }: { plan: OfferedPlan }) {
  const [broken, setBroken] = useState<ReadonlySet<string>>(() => new Set());
  const [edges, setEdges] = useState({ prev: false, next: false });
  const row = useRef<HTMLUListElement>(null);
  const images = (plan.images ?? []).filter((image) => !broken.has(image.url));

  useEffect(() => {
    const node = row.current;
    if (!node) return;
    const measure = () => setEdges(photoRowEdges(node));
    const wheel = (event: WheelEvent) => {
      const by = photoRowWheel(node, event);
      if (!by) return;
      event.preventDefault();
      node.scrollBy({ left: by });
    };
    measure();
    node.addEventListener('scroll', measure, { passive: true });
    node.addEventListener('wheel', wheel, { passive: false });
    const resized = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    resized?.observe(node);
    return () => {
      node.removeEventListener('scroll', measure);
      node.removeEventListener('wheel', wheel);
      resized?.disconnect();
    };
  }, [images.length]);

  if (images.length === 0) return null;
  const turn = (direction: 1 | -1) => {
    const node = row.current;
    const frame = node?.querySelector<HTMLElement>('.offer__photo:not(.offer__photo--cover)') ?? node?.firstElementChild;
    if (node) node.scrollBy({ left: direction * ((frame as HTMLElement | null)?.offsetWidth ?? 200), behavior: 'smooth' });
  };
  return (
    <div className="offer__gallery">
      <ul className="offer__photos" ref={row} aria-label={`Фото: ${plain(plan.title)}`}>
        {images.map((image, index) => (
          <li key={`${index}:${image.url}`} className={cn('offer__photo', index === 0 && 'offer__photo--cover')}>
            <img
              className="offer__image"
              src={image.url}
              alt={plain(image.caption ?? '')}
              loading="lazy"
              onError={() => setBroken((gone) => new Set(gone).add(image.url))}
            />
            {image.caption ? (
              <span className="offer__caption" aria-hidden="true">
                {plain(image.caption)}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      {edges.prev ? (
        <button type="button" className="offer__arrow offer__arrow--prev" aria-label="Предыдущие фото" onClick={() => turn(-1)}>
          ‹
        </button>
      ) : null}
      {edges.next ? (
        <button type="button" className="offer__arrow offer__arrow--next" aria-label="Следующие фото" onClick={() => turn(1)}>
          ›
        </button>
      ) : null}
    </div>
  );
}

/** The stops of one plan as one line, in order — a noted stop marked, never dropped.
 *  The mark is the card's own (`offer__stop--noted`, a «!» drawn by CSS), spoken as
 *  «есть замечание»; the note itself is printed below the line. */
function OfferStops({ plan }: { plan: Pick<OfferedPlan, 'stops'> }) {
  return (
    <div className="offer__stops">
      {plan.stops.map((stop, index) => (
        <span key={`${index}:${stop.title}`}>
          {index > 0 ? ' · ' : null}
          <span className={cn('offer__stop', stop.note && 'offer__stop--noted')}>
            {plain(stop.title)}
            {stop.note ? <span className="visually-hidden"> (есть замечание)</span> : null}
          </span>
        </span>
      ))}
    </div>
  );
}

/** «Большой каскад — фонтан не работает: …», one per noted stop. */
export function offerStopNotes(plan: Pick<OfferedPlan, 'stops'>): string[] {
  return plan.stops.filter((stop) => stop.note).map((stop) => plain(`${stop.title} — ${stop.note}`));
}

export default function PlanOffer({ props }: { props: PlanOfferProps }) {
  return (
    <article className="card card--offer">
      <header className="card__head">
        <h3 className="card__title">{plain(props.title ?? 'Подходящие планы')}</h3>
        {props.date || props.weather ? (
          <div className="offer__when">
            {props.date ? <span className="card__meta">{plain(props.date)}</span> : null}
            {/* The forecast for that date (#205), the code's own words; no forecast — no line. */}
            {props.weather ? <span className="card__meta offer__weather">{plain(props.weather.text)}</span> : null}
          </div>
        ) : null}
      </header>
      <ol className="plan__list">
        {props.plans.map((plan) => (
          <li key={`${plan.n}:${plan.title}`} className="plan__row offer__plan">
            <span className="plan__marker plan__marker--num">{plan.n}</span>
            <div className="plan__body">
              <OfferPhotos plan={plan} />
              <div className="plan__head">
                <span className="plan__title">{plain(plan.title)}</span>
                {plan.warning ? <span className="plan__badge">{plain(plan.warning)}</span> : null}
              </div>
              {plan.summary ? <div className="plan__subtitle">{plain(plan.summary)}</div> : null}
              <OfferStops plan={plan} />
              {offerStopNotes(plan).map((note) => (
                <div key={note} className="plan__note">
                  {note}
                </div>
              ))}
            </div>
          </li>
        ))}
      </ol>
    </article>
  );
}
